import React from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, LabelList } from "recharts";
import styled from "../style.module.css";
import { ACCENTS, AXIS_TICK, TOOLTIP_STYLE } from "./theme";

// Horizontal top-N bars with end-of-bar value labels and a subtle per-bar
// color gradient (top item brightest). `data` rows mapped to { name, value }.
const BreakdownBars = ({
  title,
  rows,
  nameKey,
  valueKey,
  formatValue,
  emptyHint,
  asOf = true,
  color = ACCENTS[0],
  mono = true,
  // Optional: `info` = hover guidance shown via an ⓘ next to the title;
  // `valueLabel` = what the number is (e.g. "Views"), shown in the bar tooltip.
  info,
  valueLabel,
}) => {
  const data = (rows || [])
    .map((r) => ({ name: r[nameKey], value: Number(r[valueKey]) || 0 }))
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  const fmt = (v) => (formatValue ? formatValue(v) : Number(v).toLocaleString());

  // Mono ramp: a single hue, brightest at top and fading down (ordered feel);
  // or distinct accent colors per bar when mono=false.
  const barColor = (i) => (mono ? color : ACCENTS[i % ACCENTS.length]);
  const barOpacity = (i) => (mono ? 1 - (i / Math.max(data.length, 1)) * 0.55 : 1);

  // Recharts wraps long category labels onto multiple lines by default, which
  // overlaps neighboring bars. Truncate to one line instead; the full name is
  // still available via the Tooltip on hover.
  const MAX_LABEL_CHARS = 18;
  const renderYAxisTick = ({ x, y, payload }) => {
    const raw = String(payload.value ?? "");
    const label = raw.length > MAX_LABEL_CHARS ? `${raw.slice(0, MAX_LABEL_CHARS - 1)}…` : raw;
    return (
      <text x={x} y={y} dy={4} textAnchor="end" fill={AXIS_TICK.fill} fontSize={AXIS_TICK.fontSize}>
        {label}
      </text>
    );
  };

  return (
    <div className={styled.chartCard}>
      <div className={styled.chartCardHead}>
        <h3 className={styled.chartTitle}>
          {title}
          {info && (
            <span
              title={info}
              aria-label={info}
              role="img"
              style={{ marginLeft: 8, fontSize: 14, fontWeight: 400, opacity: 0.6, cursor: "default" }}
            >
              ⓘ
            </span>
          )}
        </h3>
        {asOf && <span className={styled.asOfTag}>Live</span>}
      </div>
      {data.length > 0 ? (
        <ResponsiveContainer width="100%" height={Math.max(180, data.length * 38)} minWidth={0}>
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 56, bottom: 4, left: 8 }}>
            <XAxis type="number" hide domain={[0, "dataMax"]} />
            <YAxis
              type="category"
              dataKey="name"
              tick={renderYAxisTick}
              tickLine={false}
              axisLine={false}
              width={130}
            />
            <Tooltip
              {...TOOLTIP_STYLE}
              cursor={{ fill: "rgba(255,255,255,0.05)" }}
              // Without a name recharts rendered a dangling ": 152".
              separator={valueLabel ? ": " : ""}
              formatter={(v) => [fmt(v), valueLabel || ""]}
            />
            <Bar dataKey="value" radius={[0, 5, 5, 0]} barSize={18}>
              {data.map((d, i) => (
                <Cell key={i} fill={barColor(i)} fillOpacity={barOpacity(i)} />
              ))}
              <LabelList
                dataKey="value"
                position="right"
                formatter={fmt}
                style={{ fill: "rgba(255,255,255,0.78)", fontSize: 11, fontWeight: 600 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <p className={styled.emptyHint}>{emptyHint || "No data yet."}</p>
      )}
    </div>
  );
};

export default BreakdownBars;
